import type { PinRequiredSpace } from '@models/api';
import {
  isRegistrationCelebration,
  isRegistrationSurface,
  type RegistrationCelebration,
  type RegistrationSurface,
} from '@utils/registrationCelebration';

/**
 * What the `event_pin` celebration needs, carried on the celebration intent so it survives a
 * navigation or a post-sign-in reload: the PIN spaces in host order (as the server listed them),
 * the ticket question to ask afterwards (null when the server did not ask), and where Going was
 * tapped.
 */
export interface EventPinCelebration {
  event_id: string;
  spaces: PinRequiredSpace[];
  registration: RegistrationCelebration | null;
  surface: RegistrationSurface;
  /**
   * Set when the Event page reopened the sheet from a live join hold (useEventJoinHoldsReopen),
   * not a Going tap. `surface` cannot tell them apart: a Going on the Event page is `event_page`
   * too. Analytics report it as `join_surface: 'event_page_reopen'`.
   */
  reopen?: true;
}

/**
 * Shape check for one PIN Space, from the server or from storage. Every producer of an `event_pin`
 * intent filters through it, so a malformed item never reaches an intent or the sheet.
 */
export function isPinRequiredSpace(value: unknown): value is PinRequiredSpace {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.space_id === 'string' &&
    v.space_id !== '' &&
    typeof v.space_name === 'string' &&
    typeof v.space_slug === 'string' &&
    (v.logo_url === null || typeof v.logo_url === 'string') &&
    typeof v.release_at === 'string'
  );
}

/** Shape check for a stored payload: a malformed one must never reach the sheet. */
export function isEventPinCelebration(value: unknown): value is EventPinCelebration {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.event_id === 'string' &&
    v.event_id !== '' &&
    Array.isArray(v.spaces) &&
    v.spaces.length > 0 &&
    v.spaces.every(isPinRequiredSpace) &&
    (v.registration === null || isRegistrationCelebration(v.registration)) &&
    isRegistrationSurface(v.surface) &&
    (v.reopen === undefined || v.reopen === true)
  );
}
